import { performanceService } from './performanceService';
import { LEARNER_PERSIAN_FONT_OPTIONS } from '../constants/learnerPersianFonts';

export interface PersianFontConfig {
  family: string;
  weights: number[];
  display: 'auto' | 'block' | 'swap' | 'fallback' | 'optional';
  preload: boolean;
  subset?: string;
}

export interface FontLoadResult {
  family: string;
  loaded: boolean;
  loadTime: number;
  error?: string;
}

class PersianFontOptimizer {
  private loadedFonts = new Set<string>();
  private fontConfigs: PersianFontConfig[] = [
    {
      family: 'Vazir',
      weights: [300, 400, 500, 700],
      display: 'swap',
      preload: true,
      subset: 'persian'
    },
    {
      family: 'Shabnam',
      weights: [300, 400, 500, 700],
      display: 'swap',
      preload: false,
      subset: 'persian'
    },
    {
      family: 'Samim',
      weights: [400, 500],
      display: 'swap',
      preload: false,
      subset: 'persian'
    }
  ];

  constructor() {
    this.initializeFontOptimization();
  }

  /**
   * Initialize font optimization
   */
  private async initializeFontOptimization(): Promise<void> {
    // Preload critical fonts
    await this.preloadCriticalFonts();
    
    // Set up font loading strategies
    this.setupFontLoadingStrategies();
    
    // Monitor font loading performance
    this.monitorFontPerformance();
  }

  /**
   * Preload critical Persian fonts
   */
  async preloadCriticalFonts(): Promise<FontLoadResult[]> {
    const criticalFonts = this.fontConfigs.filter(config => config.preload);
    const results: FontLoadResult[] = [];

    for (const config of criticalFonts) {
      const result = await this.loadFont(config);
      results.push(result);
    }

    return results;
  }

  /**
   * Load a specific font with performance tracking
   */
  async loadFont(config: PersianFontConfig): Promise<FontLoadResult> {
    const startTime = performance.now();
    const fontKey = `${config.family}-${config.weights.join('-')}`;

    if (this.loadedFonts.has(fontKey)) {
      return {
        family: config.family,
        loaded: true,
        loadTime: 0
      };
    }

    try {
      // Create font face declarations
      await this.createFontFaces(config);
      
      // Load fonts using Font Loading API
      await this.loadFontFaces(config);
      
      this.loadedFonts.add(fontKey);
      
      const loadTime = performance.now() - startTime;
      performanceService.recordMetric(`font_load_${config.family}`, loadTime);

      return {
        family: config.family,
        loaded: true,
        loadTime
      };
    } catch (error) {
      const loadTime = performance.now() - startTime;
      
      return {
        family: config.family,
        loaded: false,
        loadTime,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Create font face declarations
   */
  private async createFontFaces(config: PersianFontConfig): Promise<void> {
    const styleId = `persian-font-${config.family.toLowerCase()}`;
    
    // Remove existing style if present
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Temporarily disabled to prevent PostCSS errors
    // const style = document.createElement('style');
    // style.id = styleId;
    // 
    // let css = '';
    // 
    // for (const weight of config.weights) {
    //   const fontUrl = this.getFontUrl(config.family, weight, config.subset);
    //   
    //   css += `
    //     @font-face {
    //       font-family: '${config.family}';
    //       font-style: normal;
    //       font-weight: ${weight};
    //       font-display: ${config.display};
    //       src: url('${fontUrl}') format('woff2');
    //       unicode-range: U+0600-06FF, U+200C-200E, U+2010-2011, U+204F, U+2E41, U+FB50-FDFF, U+FE80-FEFC;
    //     }
    //   `;
    // }
    // 
    // style.textContent = css;
    // document.head.appendChild(style);
  }

  /**
   * Load font faces using Font Loading API
   */
  private async loadFontFaces(_config: PersianFontConfig): Promise<void> {
    if (!('fonts' in document)) {
      return this.fallbackFontLoad(_config);
    }

    const bundled = LEARNER_PERSIAN_FONT_OPTIONS.filter((o): o is (typeof o & { cssFamily: string }) => !!o.cssFamily);
    const weights = _config.weights.length ? _config.weights : [400];
    await Promise.all(
      bundled.flatMap((o) =>
        weights.map((weight) =>
          document.fonts.load(`${weight} 16px "${o.cssFamily}"`).catch(() => undefined)
        )
      )
    );
  }

  /**
   * Fallback font loading for older browsers
   */
  private async fallbackFontLoad(config: PersianFontConfig): Promise<void> {
    return new Promise((resolve, reject) => {
      const testText = 'سلام دنیا';
      const testElement = document.createElement('div');
      
      testElement.style.cssText = `
        position: absolute;
        left: -9999px;
        top: -9999px;
        font-size: 72px;
        font-family: monospace;
        visibility: hidden;
      `;
      
      testElement.textContent = testText;
      document.body.appendChild(testElement);
      
      const initialWidth = testElement.offsetWidth;
      
      testElement.style.fontFamily = `'${config.family}', monospace`;
      
      let attempts = 0;
      const maxAttempts = 50;
      
      const checkFont = () => {
        attempts++;
        
        if (testElement.offsetWidth !== initialWidth) {
          document.body.removeChild(testElement);
          resolve();
        } else if (attempts >= maxAttempts) {
          document.body.removeChild(testElement);
          reject(new Error(`Font ${config.family} failed to load`));
        } else {
          setTimeout(checkFont, 100);
        }
      };
      
      checkFont();
    });
  }

  /**
   * Legacy hook: app fonts are bundled; no remote font URLs.
   */
  private getFontUrl(_family: string, _weight: number, _subset?: string): string {
    return '';
  }

  /**
   * Setup font loading strategies
   */
  private setupFontLoadingStrategies(): void {
    // Critical font loading strategy
    this.applyCriticalFontStrategy();
    
    // Progressive font enhancement
    this.setupProgressiveEnhancement();
    
    // Font loading timeout handling
    this.setupFontLoadingTimeout();
  }

  /**
   * Apply critical font loading strategy
   */
  private applyCriticalFontStrategy(): void {
    const criticalCSS = `
      .font-persian {
        font-family: 'Vazir', 'Tahoma', sans-serif;
      }
      
      .font-persian-heading {
        font-family: 'Shabnam', 'Vazir', 'Tahoma', sans-serif;
      }
      
      .font-persian-body {
        font-family: 'Samim', 'Vazir', 'Tahoma', sans-serif;
      }
    `;

    // Temporarily disabled to prevent PostCSS errors
    // const style = document.createElement('style');
    // style.textContent = criticalCSS;
    // document.head.appendChild(style);
  }

  /**
   * Setup progressive font enhancement
   */
  private setupProgressiveEnhancement(): void {
    // Add font-loaded class when fonts are ready
    if ('fonts' in document) {
      document.fonts.ready.then(() => {
        document.documentElement.classList.add('fonts-loaded');
        performanceService.recordMetric('all_fonts_loaded', performance.now());
      });
    }
  }

  /**
   * Setup font loading timeout
   */
  private setupFontLoadingTimeout(): void {
    // Add timeout class if fonts take too long
    setTimeout(() => {
      if (!document.documentElement.classList.contains('fonts-loaded')) {
        document.documentElement.classList.add('fonts-timeout');
      }
    }, 3000); // 3 second timeout
  }

  /**
   * Monitor font loading performance
   */
  private monitorFontPerformance(): void {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name.includes('font')) {
              performanceService.recordMetric('font_resource_load', entry.duration);
            }
          }
        });
        
        observer.observe({ entryTypes: ['resource'] });
      } catch (error) {
        console.warn('Font performance monitoring not supported:', error);
      }
    }
  }

  /**
   * Optimize text rendering for Persian content
   */
  optimizeTextRendering(): void {
    const optimizationCSS = `
      .persian-text {
        text-rendering: optimizeLegibility;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        font-feature-settings: "kern" 1, "liga" 1;
      }
      
      .persian-text-performance {
        text-rendering: optimizeSpeed;
        -webkit-font-smoothing: subpixel-antialiased;
      }
      
      @media (max-width: 768px) {
        .persian-text {
          text-rendering: optimizeSpeed;
        }
      }
    `;

    // Temporarily disabled to prevent PostCSS errors
    // const style = document.createElement('style');
    // style.textContent = optimizationCSS;
    // document.head.appendChild(style);
  }

  /**
   * Get font loading status
   */
  getFontLoadingStatus(): Record<string, boolean> {
    const status: Record<string, boolean> = {};
    
    for (const config of this.fontConfigs) {
      const fontKey = `${config.family}-${config.weights.join('-')}`;
      status[config.family] = this.loadedFonts.has(fontKey);
    }
    
    return status;
  }

  /**
   * Preload fonts for specific content
   */
  async preloadFontsForContent(content: string): Promise<void> {
    const needsPersianFont = /[\u0600-\u06FF]/.test(content);

    if (needsPersianFont) {
      const bundled = LEARNER_PERSIAN_FONT_OPTIONS.filter((o): o is (typeof o & { cssFamily: string }) => !!o.cssFamily);
      if ('fonts' in document) {
        await Promise.all(
          bundled.map((o) => document.fonts.load(`400 16px "${o.cssFamily}"`).catch(() => undefined))
        );
      }
    }
  }

  /**
   * Clean up font optimizer
   */
  destroy(): void {
    this.loadedFonts.clear();
  }
}

// Create singleton instance
export const persianFontOptimizer = new PersianFontOptimizer();

export default PersianFontOptimizer;